// Thin facade over Convex client calls + the few remaining Bearer-token
// fetches (data export).

import { ConvexError } from 'convex/values';
import { api as convexApi } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import type { FunctionArgs, FunctionReturnType } from 'convex/server';
import { getConvexSignIn, getConvexSignOut } from './auth/convexBridge';
import { clearProfileIntent } from './pendingProfileIntent';
import { convex, convexSiteURL, currentUserStore } from './convexDispatch';

// APIError survives for two throw sites in this module (convexMeAndStore
// null + R2 upload PUT failure). Every other caller has moved to
// ConvexError + errorCode(). Drop the class once those two
// throw sites also migrate.
export class APIError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

// Recognise Convex Auth's credential-failure errors. The Password
// provider throws a bare `InvalidAccountId` (for both unknown-email and
// wrong-password, intentionally indistinguishable so we don't leak
// which emails are registered) or `InvalidSecret`; these surface with
// no structured `code`, so friendlyError() falls through to the generic
// message. We sniff the message string so signin() can re-throw a clean
// INVALID_CREDENTIALS instead.
function isInvalidCredentialsError(e: unknown): boolean {
  const msg =
    e instanceof Error
      ? e.message
      : typeof e === 'string'
        ? e
        : (() => {
            const data = (e as { data?: unknown })?.data;
            return typeof data === 'string' ? data : '';
          })();
  return /InvalidAccountId|InvalidSecret|InvalidCredentials/i.test(msg);
}

// ── session store (localStorage backed; httpOnly cookies would be cleaner
//    but require a same-origin proxy at the edge — TBD before launch) ────

// Schema-derived. Shape (camelCase + epoch-ms timestamps + required
// dob:"") comes straight from users.me — no hand-typed mirror to drift.
// PlanBadge's cancels/ended/renews-on annotations rely on
// subscriptionCancelled + subscriptionExpiresAt only; the older
// subscription_ended_at / subscription_renews_at fields were never
// emitted by Convex me().
export type User = NonNullable<FunctionReturnType<typeof convexApi.users.me>>;

const KEY_ACCESS  = 'wydm.session.access';
const KEY_REFRESH = 'wydm.session.refresh';
const KEY_USER    = 'wydm.session.user';

export const sessionStore = {
  getAccess(): string | null { return localStorage.getItem(KEY_ACCESS); },
  getRefresh(): string | null { return localStorage.getItem(KEY_REFRESH); },
  getUser(): User | null {
    const raw = localStorage.getItem(KEY_USER);
    return raw ? (JSON.parse(raw) as User) : null;
  },
  set(access: string, refresh: string, user: User) {
    localStorage.setItem(KEY_ACCESS, access);
    localStorage.setItem(KEY_REFRESH, refresh);
    localStorage.setItem(KEY_USER, JSON.stringify(user));
  },
  clear() {
    localStorage.removeItem(KEY_ACCESS);
    localStorage.removeItem(KEY_REFRESH);
    localStorage.removeItem(KEY_USER);
  },
};

// ── typed API surface ───────────────────────────────────────────────────

async function convexMeAndStore(): Promise<User> {
  const user = await convex.query(convexApi.users.me, {});
  if (!user) {
    // Mirror src/app/lib/api.ts — DON'T eagerly sign out on null.
    // me() returning null has two causes: (1) auth-propagation race
    // post-signin (next tick succeeds), or (2) true ghost session
    // (server-side user row deleted). Eagerly signing out punishes
    // case (1) and produces the "signin loading then nothing"
    // failure mode where the user lands back on /auth/signin with
    // no error. The global AuthGate (lib/auth/AuthGate.tsx) handles
    // case (2) reactively via useConvexAuth() observation.
    throw new APIError(401, 'UNAUTHORIZED', 'no authenticated user');
  }
  currentUserStore.set(user);
  return user;
}

/**
 * meOrNull — bounded, NON-throwing me() fetch. Mirror of the helper of the
 * same name in src/app/lib/api.ts.
 *
 * Returns the real User once Convex Auth's identity has propagated to the
 * query layer, or null if it hasn't within the retry budget. Unlike
 * convexMeAndStore (exposed as api.me) this never throws UNAUTHORIZED on a
 * null result, so callers that only need to ROUTE can use it: a transient
 * identity-propagation lag degrades to a fallback route instead of an
 * error.
 *
 * This exists because the signup/OAuth wrappers below return a SENTINEL
 * User whose `profileComplete` is hardcoded false (identity hasn't
 * propagated yet, so there is nothing real to report). Any caller deciding
 * a destination from `pair.user.profileComplete` therefore always saw
 * false and sent complete users to the builder. Route from this instead.
 */
async function meOrNull(): Promise<User | null> {
  for (let i = 0; i < 5; i++) {
    try {
      const u = await convex.query(convexApi.users.me, {});
      if (u) {
        currentUserStore.set(u);
        return u;
      }
    } catch {
      /* best-effort — fall through to the next attempt */
    }
    await new Promise((r) => setTimeout(r, 200 * (i + 1)));
  }
  return null;
}

// Convex Auth manages its own token storage via @convex-dev/auth/react's
// React provider (localStorage on web). sessionStore's access/refresh
// slots are filled with empty strings here — kept for cross-launch User
// cache continuity, not as a token source.
function convexTokenSentinel(user: User): TokenPair {
  return { access: '', refresh: '', expires_at: '', user };
}

// Shape the SPA expects right after a sign-in/up call returns but
// BEFORE Convex Auth's identity has propagated to the query layer.
// Real values are picked up by destination screens via
// useQuery(api.users.me). Empty `id` is the sentinel marker the
// SignUp screen branches on to decide /auth/verify vs /home.
//
// ⚠️ Every field below except `email` is a PLACEHOLDER, not a fact. In
// particular `profileComplete: false` is unconditional — identity hasn't
// propagated, so there is nothing real to report yet. Do NOT branch on
// these values to pick a post-auth destination; a returning user with a
// complete profile would be sent to the builder. Use `api.meOrNull()`,
// which resolves the real row (or null) without throwing on the race.
function sentinelUser(email: string): User {
  return {
    id: '' as User['id'],
    email,
    displayName: '',
    dob: '',
    tier: 'free',
    plan: 'none',
    referralPremiumUntil: null,
    premiumSource: 'none',
    profileComplete: false,
    status: 'active',
    emailVerified: false,
    createdAt: Date.now(),
    subscriptionCancelled: false,
    subscriptionExpiresAt: null,
    // Sentinel default — real value arrives with the next me() fetch.
    // No consent gate during the sentinel window; the reactive me()
    // pops it if needed once identity propagates.
    consent: { needsPrivacy: false, needsTerms: false, needsConsent: false },
    // 'prod' so no dev-only affordance flashes during the sentinel window.
    environment: 'prod',
  };
}

export const api = {
  // Convex Auth's Password provider signUp triggers the verify-email
  // flow: no session is issued until the user submits the
  // 6-digit code. SPA's post-signup flow routes to the verify-email
  // screen using the returned stub.
  signup: async (body: { email: string; password: string; terms_accepted: boolean; referralCode?: string; attributionSlug?: string; utmSource?: string; utmCampaign?: string }): Promise<TokenPair> => {
    // Pre-flight duplicate-email check (see users.signupEmailConflict).
    // The Password provider's authorize() still throws EMAIL_TAKEN /
    // EMAIL_LINKED_ELSEWHERE as the authoritative backstop, but that
    // throw propagates out of the Convex Auth action and Convex logs it
    // as an uncaught "Server Error" on every duplicate signup. Checking
    // here lets us surface the SAME ConvexError to SignUp.tsx's existing
    // catch (errorCode → EMAIL_TAKEN / EMAIL_LINKED_ELSEWHERE) without
    // ever calling signIn in the common case, so nothing gets logged.
    const conflict = await convex.query(convexApi.users.signupEmailConflict, {
      email: body.email,
    });
    if (conflict) {
      if (conflict.oauthProvider && !conflict.hasPassword) {
        throw new ConvexError({
          code: 'EMAIL_LINKED_ELSEWHERE',
          provider: conflict.oauthProvider,
        });
      }
      throw new ConvexError({ code: 'EMAIL_TAKEN' });
    }
    await getConvexSignIn()('password', {
      flow: 'signUp',
      email: body.email,
      password: body.password,
      // Optional inbound referral code. The Convex Auth
      // Password provider forwards extra params to authorize(), where
      // attribution runs server-side after the user row is created
      // (functions/auth.ts → users.attributeReferral). Omitted entirely
      // when absent so we never pass an empty code.
      ...(body.referralCode ? { referralCode: body.referralCode } : {}),
      // Creator campaign attribution (creators.attributeCreatorSignup,
      // same authorize() slot). The card slug the visitor landed on +
      // any boosted-post UTMs; omitted when absent.
      ...(body.attributionSlug ? { attributionSlug: body.attributionSlug } : {}),
      ...(body.utmSource ? { utmSource: body.utmSource } : {}),
      ...(body.utmCampaign ? { utmCampaign: body.utmCampaign } : {}),
    });
    return convexTokenSentinel({
      id: '' as User['id'],
      email: body.email,
      displayName: '',
      dob: '',
      tier: 'free',
      plan: 'none',
      referralPremiumUntil: null,
      premiumSource: 'none',
      profileComplete: false,
        status: 'active',
      emailVerified: false,
      createdAt: Date.now(),
      subscriptionCancelled: false,
      subscriptionExpiresAt: null,
      consent: { needsPrivacy: false, needsTerms: false, needsConsent: false },
      environment: 'prod',
    });
  },
  // signin / oauthApple / oauthGoogle — see mirror docstring in
  // src/app/lib/api.ts: intentionally don't await me() here because
  // Convex Auth's client identity propagates a tick after the
  // signIn action returns. Destination screens pick up the real
  // user via useQuery(api.users.me).
  signin: async (body: { email: string; password: string }): Promise<TokenPair> => {
    try {
      await getConvexSignIn()('password', {
        flow: 'signIn',
        email: body.email,
        password: body.password,
      });
    } catch (e) {
      // Wrong email/password is a KNOWN unhappy path. Convex Auth's
      // Password provider throws a bare InvalidAccountId / InvalidSecret
      // with no structured `code`, so friendlyError() would render the
      // generic "Something went wrong." Translate it into a recognisable
      // INVALID_CREDENTIALS the screen surfaces as "Wrong email or
      // password."
      if (isInvalidCredentialsError(e)) {
        throw new ConvexError({ code: 'INVALID_CREDENTIALS' });
      }
      throw e;
    }
    return convexTokenSentinel(sentinelUser(body.email));
  },
  oauthApple: async (body: { id_token: string; terms_accepted?: boolean; referralCode?: string; attributionSlug?: string; utmSource?: string; utmCampaign?: string }): Promise<TokenPair> => {
    await getConvexSignIn()('apple', {
      id_token: body.id_token,
      ...(body.terms_accepted !== undefined
        ? { terms_accepted: body.terms_accepted }
        : {}),
      // Creator attribution params, forwarded to the native
      // provider's authorize (see providers/nativeApple.ts). Referral
      // REWARDS stay password-only; a code here counts for campaigns.
      ...(body.referralCode ? { referralCode: body.referralCode } : {}),
      ...(body.attributionSlug ? { attributionSlug: body.attributionSlug } : {}),
      ...(body.utmSource ? { utmSource: body.utmSource } : {}),
      ...(body.utmCampaign ? { utmCampaign: body.utmCampaign } : {}),
    });
    return convexTokenSentinel(sentinelUser(''));
  },
  oauthGoogle: async (body: { id_token: string; terms_accepted?: boolean; referralCode?: string; attributionSlug?: string; utmSource?: string; utmCampaign?: string }): Promise<TokenPair> => {
    await getConvexSignIn()('google', {
      id_token: body.id_token,
      ...(body.terms_accepted !== undefined
        ? { terms_accepted: body.terms_accepted }
        : {}),
      // Creator attribution params, forwarded to the native
      // provider's authorize (see providers/nativeGoogle.ts). Referral
      // REWARDS stay password-only; a code here counts for campaigns.
      ...(body.referralCode ? { referralCode: body.referralCode } : {}),
      ...(body.attributionSlug ? { attributionSlug: body.attributionSlug } : {}),
      ...(body.utmSource ? { utmSource: body.utmSource } : {}),
      ...(body.utmCampaign ? { utmCampaign: body.utmCampaign } : {}),
    });
    return convexTokenSentinel(sentinelUser(''));
  },
  // Pre-launch waitlist capture. Public, unauthenticated. Used by the
  // "WYDM isn't accepting sign-ups yet" modal when signup endpoints
  // return SIGNUPS_DISABLED. Idempotent on email. Fetches the Convex
  // httpAction directly (no API_BASE prefix).
  waitlist: async (body: { email: string; source?: string }): Promise<{ ok: true }> => {
    const res = await fetch(convexSiteURL() + '/auth/waitlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
      throw new APIError(
        res.status,
        data?.error?.code ?? 'WAITLIST_FAILED',
        data?.error?.message ?? `Server responded with ${res.status}`,
      );
    }
    return { ok: true };
  },
  me: (): Promise<User> => convexMeAndStore(),
  // Route-only variant — never throws on the identity-propagation race.
  // Use this (not `pair.user`) to decide a post-auth destination.
  meOrNull: (): Promise<User | null> => meOrNull(),
  // Soft-delete the caller's account. 30-day grace before hard purge;
  // active subscriptions auto-cancel at period end. Backend revokes
  // all sessions immediately, so the client should clear local
  // storage + bounce to landing after this resolves.
  deleteAccount: async (
    body: { reason?: string },
  ): Promise<{ ok: true; deleted_at: string; hard_delete_after: string }> => {
    const r = await convex.mutation(convexApi.users.deleteAccount, {
      ...(body.reason ? { reason: body.reason } : {}),
    });
    return {
      ok: true as const,
      deleted_at: new Date(r.deletedAt).toISOString(),
      hard_delete_after: new Date(r.hardDeleteAfter).toISOString(),
    };
  },
  // GDPR Art. 20 data export. Returns a Blob the caller triggers as a
  // file download — Convex httpAction, bearer-token auth via Convex
  // Auth's stored access token. Rate-limited 3/day per user; 429
  // surfaces here as an APIError.
  async downloadDataExport(): Promise<Blob> {
    const access = sessionStore.getAccess();
    const res = await fetch(convexSiteURL() + '/auth/account/data-export', {
      headers: access ? { Authorization: 'Bearer ' + access } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
      throw new APIError(
        res.status,
        body?.error?.code ?? 'EXPORT_FAILED',
        body?.error?.message ?? `Server responded with ${res.status}`,
      );
    }
    return res.blob();
  },
  // Both fields are optional — only updates what's provided. Use for
  // signup completion (both set) AND for in-builder edits to either.
  completeProfile: async (body: { dob?: string; display_name?: string }): Promise<User> => {
    await convex.mutation(convexApi.users.completeProfile, {
      ...(body.dob ? { dob: body.dob } : {}),
      ...(body.display_name ? { displayName: body.display_name } : {}),
    });
    return convexMeAndStore();
  },
  // Auth-gated server-side;
  // no-ops for already-verified or OAuth-signup users.
  resendVerificationEmail: (): Promise<{ ok: true; sent: boolean }> =>
    convex.action(convexApi.users.resendVerificationEmail, {}) as Promise<{
      ok: true;
      sent: boolean;
    }>,
  // verifyEmail — submits the 6-digit code to our own
  // users.verifyEmailCode mutation. See src/app/lib/api.ts header
  // for full design rationale (we own the code lifecycle in
  // users.ts now rather than routing through Convex Auth's signIn
  // flow). On success the user is already signed in (password
  // signUp issues a session immediately), so we just refresh their
  // cached me() to surface emailVerified=true.
  verifyEmail: async (body: { code: string }): Promise<TokenPair> => {
    await convex.mutation(convexApi.users.verifyEmailCode, { code: body.code });
    return convexTokenSentinel(await convexMeAndStore());
  },
  signout: async (): Promise<void> => {
    try {
      await getConvexSignOut()();
    } finally {
      currentUserStore.clear();
      // Drop any unposted comment draft. Closes the
      // shared-device hole: visitor A abandons a draft at the sign-up
      // gate, visitor B signs in in the same browser and would otherwise
      // find A's text pre-filled, ready to post under B's name.
      clearProfileIntent();
    }
  },
  // Convex Auth's ResetEmailProvider — flow:"reset" generates the
  // code + emails it. The actual reset happens via passwordResetConfirm
  // below (flow:"reset-verification"). Returns ok:true regardless of
  // whether the email exists (anti-enumeration).
  passwordResetRequest: async (email: string): Promise<{ ok: true }> => {
    await getConvexSignIn()('password', { flow: 'reset', email });
    return { ok: true };
  },
  // flow:"reset-verification" validates the code + sets the new
  // password in one shot. On success we deliberately do NOT issue a
  // session — the user re-enters credentials.
  passwordResetConfirm: async (body: {
    email: string;
    code: string;
    new_password: string;
  }): Promise<{ ok: true }> => {
    await getConvexSignIn()('password', {
      flow: 'reset-verification',
      email: body.email,
      code: body.code,
      newPassword: body.new_password,
    });
    return { ok: true };
  },
};

// ── support ───────────────────────────────────────────────────────────
//
// Convex-only. Ingests a support message directly into the support inbox
// (supportThreads) on the signed-in user's behalf; admin replies go back
// out as threaded email from the support address. Settings "Contact
// support" links call this.

export const supportApi = {
  submitMessage: (body: { subject: string; body: string }): Promise<{ ok: true }> =>
    convex.action(convexApi.supportInbox.submitMessage, body) as Promise<{ ok: true }>,
};

export interface TokenPair {
  access: string;
  refresh: string;
  expires_at: string;
  user: User;
}

// ── profile types ──────────────────────────────────────────────────────────

// Schema-derived. Profile is profiles.me's return shape — already
// snake_case + epoch-ms server-side, so screens consume it verbatim.
//
// Gender + Visibility narrow back to their literal unions via
// NonNullable on the inferred profile field; LookingFor stays
// hand-typed because the Convex schema stores `lookingFor` as
// `v.array(v.string())` (no literal union), so inference would only
// give `string[]`. Callers cast at the read boundary.
export type Profile = NonNullable<FunctionReturnType<typeof convexApi.profiles.me>>;
export type Gender = NonNullable<Profile['gender']>;
export type Visibility = Profile['visibility'];
export type LookingFor = 'men' | 'women' | 'everyone';

// ProfilePatch derives from the update mutation's arg shape (camelCase
// — `lookingFor`, not `looking_for` — because Convex mutations don't
// snake-case args). Callers build the patch in Convex's shape directly.
export type ProfilePatch = FunctionArgs<typeof convexApi.profiles.update>;

// ── public (auth-free) profile read ────────────────────────────────────────

// Schema-derived types — inferred from the Convex action's return shape
// instead of hand-typed. If the Convex side renames a field or flips a
// nullable, every call site updates automatically.
export type SpotifyConnection = FunctionReturnType<typeof convexApi.spotify.me>;
export type SpotifyTrack = SpotifyConnection['tracks'][number];
export type SpotifyArtist = SpotifyConnection['artists'][number];

// PublicProfile is publicBySlug's return shape with music/music_artists
// re-typed: Convex stores Spotify cache rows as v.any(), so inference
// would give `any[]` there and the MusicCards consumer would lose its
// SpotifyTrack/SpotifyArtist field shape. Override at the type boundary.
type PublicProfileRaw = NonNullable<FunctionReturnType<typeof convexApi.profiles.publicBySlug>>;
export type PublicProfile = Omit<PublicProfileRaw, 'music' | 'music_artists'> & {
  music: SpotifyTrack[];
  music_artists: SpotifyArtist[];
};
export type PublicPhoto = PublicProfile['photos'][number];

// What the profile view renders: the public DTO without the viewer-specific
// fields. /pub/:slug (the search-engine page) is served exactly this.
export type ProfileViewData = Omit<PublicProfile, 'user_id' | 'rating_count' | 'yes_percent'>;

export const publicApi = {
  // Thin wrapper over api.profiles.reportProfile. Kept here so the
  // ReportProfileModal stays Convex-agnostic at its call site (matches
  // the rest of publicApi). The mutation itself enforces auth,
  // self-report guard, and a 5/day rate limit per caller.
  report: async (
    slug: string,
    body: {
      classification: ReportClassification;
      comment: string;
      alsoBlock?: boolean;
    },
  ): Promise<{ ok: true; blocked: boolean }> =>
    convex.mutation(convexApi.profiles.reportProfile, {
      slug,
      classification: body.classification,
      comment: body.comment,
      ...(body.alsoBlock !== undefined ? { alsoBlock: body.alsoBlock } : {}),
    }),
};

export type ReportClassification =
  | 'inappropriate_photos'
  | 'fake_profile'
  | 'harassment'
  | 'spam'
  | 'underage'
  | 'other';

// Label order for the report-modal radio list. Order = display order.
export const REPORT_CLASSIFICATIONS: { id: ReportClassification; label: string; hint?: string }[] = [
  { id: 'inappropriate_photos', label: 'Inappropriate photos', hint: 'Nudity, sexual content' },
  { id: 'fake_profile',         label: 'Fake profile', hint: 'Stolen photos, catfish' },
  { id: 'harassment',           label: 'Harassment or offensive content' },
  { id: 'spam',                 label: 'Spam or scam' },
  { id: 'underage',             label: 'Looks under 18' },
  { id: 'other',                label: 'Other', hint: 'Add details below' },
];

export const REPORT_COMMENT_MAX = 1000;

// Canonical plan ordering — monthly → yearly → lifetime, then any
// unrecognised shape at the end. The backend returns plans in whatever
// order Stripe + RC hand them back, which isn't stable; we sort
// client-side so the UI is predictable regardless of vendor wobble.
const PLAN_RANK: Record<string, number> = {
  monthly:  0,
  yearly:   1,
  lifetime: 2,
};

function planRank(p: BillingPlan): number {
  if (p.kind === 'one_time') return PLAN_RANK.lifetime;
  if (p.interval === 'month') return PLAN_RANK.monthly;
  if (p.interval === 'year') return PLAN_RANK.yearly;
  return 99; // unknown shapes (eg. weekly) sort last
}

/** Sort billing plans into our canonical display order. Pure; safe to
 *  call on the array straight from the API. Preserves input order
 *  within the same rank bucket (matters when Stripe has multiple
 *  monthly prices, eg. one USD + one GBP). */
export function sortPlans(plans: BillingPlan[]): BillingPlan[] {
  return [...plans].sort((a, b) => planRank(a) - planRank(b));
}

export { RATING_NOTE_MAX } from './ratingLimits';

// ── ratings (owner-scoped dashboard shape) ────────────────────────────────

// Schema-derived from the Convex query's return shape.
export type RatingsDashboard = FunctionReturnType<typeof convexApi.ratings.dashboardForOwner>;

// Period scopes the ratings dashboard + the stats share card to a
// rolling time window. 'today' is the last 24 hours (no calendar-day
// rollover, no TZ math), 'week' the last 7 days, 'all' is open.
// Default 'all'.
export type Period = 'today' | 'week' | 'all';

// ── media types ────────────────────────────────────────────────────────────

export type MediaItem = FunctionReturnType<typeof convexApi.profileMedia.listMine>[number];
export type MediaState = MediaItem['state'];

export const mediaApi = {
  delete: async (id: string) => {
    await convex.mutation(convexApi.media.deleteMine, {
      mediaId: id as Id<'profileMedia'>,
    });
  },

  // Same-origin URL that proxies the given (R2-hosted) media URL. Used
  // by the share-card capture so the <img> + fetch are CORS-safe and
  // the canvas isn't tainted. The backend validates that the URL host
  // matches R2_PUBLIC_BASE_URL before fetching, so this isn't an open
  // proxy.
  proxyURL: (url: string): string =>
    `${convexSiteURL()}/media/proxy?url=${encodeURIComponent(url)}`,
};

/**
 * Full upload helper — handles the three-step dance:
 *   1. media:presign mutation issues a R2 PUT URL + media row
 *   2. PUT bytes directly to R2 (the backend never sees the bytes)
 *   3. media:finalize mutation HEADs R2 + inserts profileMedia +
 *      schedules NSFW + CSAM scans
 *
 * Returns the final MediaItem (state='scanning_nsfw' on success).
 */
export async function uploadPhoto(file: File, position?: number): Promise<MediaItem> {
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
    throw new APIError(400, 'UNSUPPORTED_TYPE', 'Photos must be JPEG, PNG, or WebP.');
  }
  const presigned = await convex.mutation(convexApi.media.presign, {
    content_type: file.type,
    ...(position !== undefined ? { position } : {}),
  });
  const put = await fetch(presigned.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) {
    throw new APIError(put.status, 'R2_UPLOAD_FAILED', 'Photo upload to storage failed.');
  }
  return convex.mutation(convexApi.media.finalize, {
    key: presigned.key,
    content_type: file.type,
    size_bytes: file.size,
  });
}

export const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ── notifications + push ───────────────────────────────────────────────────

export type NotificationEvent =
  | 'rating.received'
  | 'rating.with_note'
  // Deprecated (AI reviews removed) — kept for historical notification rows.
  | 'ai_review.ready'
  | 'dm.message'
  | 'media.processed'
  | 'media.rejected';

// Schema-derived from the Convex query return shapes.
export type InboxResponse = FunctionReturnType<typeof convexApi.notifications.inbox>;
export type NotificationItem = InboxResponse['items'][number];
export type NotificationCategory = 'dm' | 'other' | 'all';
export type NotificationPreferences = FunctionReturnType<typeof convexApi.notifications.preferences>;

export interface RegisterTokenBody {
  platform: 'ios' | 'android' | 'web';
  token: string;
  web_endpoint?: string;
  web_p256dh?: string;
  web_auth?: string;
  device_name?: string;
  app_version?: string;
}

export interface RegisteredToken {
  id: string;
  platform: string;
  device_name?: string;
}

// notificationsApi — thin imperative facade over the Convex
// notifications functions. Screens that want LIVE updates should call
// `useQuery(convexApi.notifications.inbox)` etc. directly; this facade
// exists for the non-React call sites (lib/push.ts) and for screens
// whose existing imperative load patterns are fine as-is.
export const notificationsApi = {
  list: (opts: { before?: number; category?: NotificationCategory } = {}) =>
    convex.query(convexApi.notifications.inbox, {
      category: opts.category ?? 'all',
      ...(opts.before !== undefined && Number.isFinite(opts.before)
        ? { before: opts.before }
        : {}),
    }) as Promise<InboxResponse>,
  markRead: (id: string) =>
    convex.mutation(convexApi.notifications.markRead, {
      id: id as Id<'notificationsLog'>,
    }),
  markAllRead: () => convex.mutation(convexApi.notifications.markAllRead, {}),
  getPrefs: () =>
    convex.query(convexApi.notifications.preferences, {}) as Promise<NotificationPreferences>,
  updatePrefs: async (prefs: NotificationPreferences) => {
    await convex.mutation(convexApi.notifications.updatePreferences, prefs);
    // Echo the input back — Convex returns { ok: true } from
    // updatePreferences; we know what we sent.
    return prefs;
  },
  registerToken: async (body: RegisterTokenBody): Promise<RegisteredToken> => {
    const r = await convex.mutation(convexApi.notifications.registerDeviceToken, {
      platform: body.platform,
      token: body.token,
      ...(body.web_endpoint ? { webEndpoint: body.web_endpoint } : {}),
      ...(body.web_p256dh ? { webP256dh: body.web_p256dh } : {}),
      ...(body.web_auth ? { webAuth: body.web_auth } : {}),
      ...(body.device_name ? { deviceName: body.device_name } : {}),
      ...(body.app_version ? { appVersion: body.app_version } : {}),
    });
    return {
      id: r.id,
      platform: r.platform,
      ...(r.device_name ? { device_name: r.device_name } : {}),
    };
  },
  unregisterToken: (id: string) =>
    convex.mutation(convexApi.notifications.unregisterDeviceToken, {
      id: id as Id<'deviceTokens'>,
    }),
};

// ── billing ─────────────────────────────────────────────────────────────
//
// Vendor-loaded plans: backend pulls them at runtime from Stripe +
// RevenueCat at /billing/plans, so the client is dumb — it renders what
// it's given and routes the upgrade tap by the `vendor` tag.

export type BillingVendor = 'stripe' | 'revenuecat';

export interface BillingPlan {
  vendor: BillingVendor;
  vendor_price_id: string;
  name: string;
  description?: string;
  unit_amount: number;
  currency: string;
  // 'recurring' (monthly/yearly Stripe Price) or 'one_time' (lifetime).
  // Drives the Checkout mode + UI: one_time renders a 'Lifetime ·
  // One-time' chip instead of '/month'.
  kind: 'recurring' | 'one_time';
  interval?: string;
  interval_count?: number;
}

// BillingPlan stays exported for lib/plans.ts's pure helpers.

// ── DMs ───────────────────────────────────────────────────────────────────
//
// Schema-derived from the Convex query return shapes. Timestamps are
// epoch ms (Convex native) — screens that need ISO render those at
// the consumption site via `new Date(n)`.
//
// `DMThread` is the single-thread detail shape (no last_message / no
// unread_count). The inbox list returns a richer per-row shape with
// those fields rolled in — surfaced separately as DMInboxEntry.

type DMInbox = FunctionReturnType<typeof convexApi.dms.listInbox>;
export type DMThread = NonNullable<FunctionReturnType<typeof convexApi.dms.getThread>>;
export type DMInboxEntry = DMInbox['threads'][number];
export type DMMessage = FunctionReturnType<typeof convexApi.dms.listMessages>['messages'][number];
export type DMThreadParty = DMThread['other_user'];

// Only `createThread` survives on web: the inbox + thread screens all
// call `convexApi.dms.*` directly via useQuery / useMutation. The one
// remaining imperative caller is PublicProfile's "Start chat" button,
// where a useMutation hook is awkward inside the click handler — a
// thin convex.mutation wrapper keeps the call site readable. The
// caller only consumes `.id` to navigate.
export const dmApi = {
  createThread: (body: { recipient_user_id?: string; recipient_slug?: string }) =>
    convex.mutation(convexApi.dms.createOrFindThread, {
      ...(body.recipient_user_id ? { recipientUserId: body.recipient_user_id as Id<'users'> } : {}),
      ...(body.recipient_slug ? { recipientSlug: body.recipient_slug } : {}),
    }),
};
